import type {
  AgentRole,
  ArchitectOutput,
  CoderOutput,
  DiffLine,
  DocumenterOutput,
  DocumenterDocUpdate,
  DocumenterReadmeUpdate,
  ExplorerOutput,
  FindingType,
  ReviewerOutput,
  RoleOutput,
  StageDetailDto,
  TesterOutput
} from '@/types/api';


interface RawStageDetail {
  stage_id: string;
  agent_id?: string;
  state: string;
  outcome?: unknown;
  started_at?: string | null;
  completed_at?: string | null;
  pipeline_run_id?: string;
  outputs?: Record<string, unknown> | null;
  findings_ledger?: {
    entries?: ReadonlyArray<Record<string, unknown>>;
    resolved_count?: number;
    remaining_count?: number;
  } | null;
  artefacts?: ReadonlyArray<{
    artefact_id?: string;
    name?: string;
    produced_by_role?: string;
    produced_by_agent_id?: string;
    produced_at?: string;
    parent_artefact_ref?: string | null;
    is_raw_output?: boolean;
  }>;
  role?: AgentRole;
  attempt_number?: number;
  provenance?: StageDetailDto['provenance'];
  output?: RoleOutput | null;
  sub_progress?: StageDetailDto['sub_progress'];
  back_edge?: StageDetailDto['back_edge'];
  partial_failure?: StageDetailDto['partial_failure'];
}

const STAGE_ROLE: Record<string, AgentRole> = {
  explore: 'explorer',
  plan: 'architect',
  code: 'coder',
  review: 'reviewer',
  test: 'tester',
  document: 'documenter'
};

const KNOWN_OUTPUT_KINDS = new Set([
  'explorer',
  'architect',
  'coder',
  'reviewer',
  'tester',
  'documenter'
]);

function roleForStage(stageId: string, fallback?: AgentRole): AgentRole {
  return STAGE_ROLE[stageId] ?? fallback ?? 'explorer';
}

const FINDING_TYPES = new Set<FindingType>([
  'file_discovery',
  'pattern_match',
  'call_chain_node',
  'entity',
  'module',
  'convention',
  'gap',
  'risk'
]);

function asFindingType(v: unknown): FindingType {
  return typeof v === 'string' && FINDING_TYPES.has(v as FindingType)
    ? (v as FindingType)
    : 'file_discovery';
}

const SUMMARY_KEYS = [
  'name', 'observation', 'description', 'implication', 'note', 'summary', 'source_text',
  'observed_structure', 'matched_requirement_shape', 'observed_role',
  'entity', 'scope_note', 'module_name', 'constraint', 'requirement_surface',
  'gap', 'impact_area', 'risk'
];

function stringifyDetailValue(v: unknown): string | null {
  if (typeof v === 'string') return v.length > 0 ? v : null;
  if (Array.isArray(v)) {
    const items = v.filter((x): x is string => typeof x === 'string' && x.length > 0);
    return items.length > 0 ? items.join(', ') : null;
  }
  return null;
}

function findingSummary(details: Record<string, unknown> | undefined, fallback: unknown): string {
  if (details) {
    const known = SUMMARY_KEYS.map((k) => stringifyDetailValue(details[k])).filter(
      (v): v is string => v !== null
    );
    if (known.length > 0) return known.slice(0, 2).join(' — ');
    const skip = new Set(['evidence_paths', 'relevant_rules_visible_by_path', 'connected_components']);
    const any = Object.entries(details)
      .filter(([k]) => !skip.has(k))
      .map(([, v]) => stringifyDetailValue(v))
      .filter((v): v is string => v !== null);
    if (any.length > 0) return any.slice(0, 2).join(' — ');
  }
  return typeof fallback === 'string' ? fallback : '';
}

function diffFromContent(content: string): DiffLine[] {
  const lines = content.split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines.map((text, i) => ({
    kind: 'add' as const,
    old_line: null,
    new_line: i + 1,
    text
  }));
}


function explorerOutput(o: Record<string, unknown>): ExplorerOutput | null {
  const report = o.exploration_report as Record<string, unknown> | undefined;
  if (!report || typeof report !== 'object') return null;
  const rawFindings = Array.isArray(report.findings) ? report.findings : [];
  const findings = rawFindings.map((f: Record<string, unknown>) => {
    const details = f.details as Record<string, unknown> | undefined;
    return {
      file_path: String(f.file_path ?? '—'),
      line_number: Number(f.line_number ?? 0),
      finding_type: asFindingType(f.finding_type),
      summary: findingSummary(details, f.summary)
    };
  });
  const coverage = report.coverage_metadata as Record<string, unknown> | undefined;
  const mode = report.analysis_mode;
  return {
    kind: 'explorer',
    summary: typeof report.summary === 'string' ? report.summary : '',
    ...(mode === 'codebase' || mode === 'requirement' || mode === 'codebase+requirement'
      ? { analysis_mode: mode }
      : {}),
    findings,
    files_inspected: Number(coverage?.files_scanned ?? findings.length)
  };
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

function architectOutput(o: Record<string, unknown>): ArchitectOutput | null {
  const plan = o.plan_document as Record<string, unknown> | undefined;
  if (!plan || typeof plan !== 'object') return null;
  const graph = plan.dependency_graph as Record<string, unknown> | undefined;
  const adjacency = graph?.adjacency as Record<string, string[]> | undefined;
  const dependsOn = new Map<string, string[]>();
  if (adjacency) {
    for (const [from, tos] of Object.entries(adjacency)) {
      for (const to of tos ?? []) {
        dependsOn.set(to, [...(dependsOn.get(to) ?? []), from]);
      }
    }
  }
  const rawTasks = Array.isArray(plan.tasks) ? plan.tasks : [];
  const tasks = rawTasks.map((t: Record<string, unknown>) => ({
    task_id: String(t.task_id ?? ''),
    title: String(t.task_description ?? t.title ?? ''),
    depends_on: dependsOn.get(String(t.task_id ?? '')) ?? [],
    acceptance_criteria: asStringArray(t.acceptance_criteria),
    target_files: asStringArray(t.target_files)
  }));
  const rawIfaces = Array.isArray(plan.interface_contracts) ? plan.interface_contracts : [];
  const interfaces = rawIfaces.map((i: Record<string, unknown>) => {
    const spec = (i.specification as Record<string, unknown> | undefined) ?? {};
    const specification: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(spec)) specification[k] = asStringArray(v);
    return {
      contract_id: String(i.contract_id ?? ''),
      name: String(i.name ?? ''),
      provider_task_id: String(i.provider_task_id ?? ''),
      consumer_task_ids: asStringArray(i.consumer_task_ids),
      interface_type: String(i.interface_type ?? ''),
      specification
    };
  });
  return {
    kind: 'architect',
    tasks,
    interfaces,
    execution_schedule: asStringArray(plan.execution_schedule),
    analysis_summary: String(plan.analysis_summary ?? ''),
    acceptance_criteria: asStringArray(plan.all_acceptance_criteria),
    plan_markdown: String(o.plan_markdown ?? '')
  };
}

function coderOutput(o: Record<string, unknown>): CoderOutput | null {
  const self_verification = coderSelfVerification(o);
  const gate = verificationGate(o);
  const extras = {
    ...(self_verification ? { self_verification } : {}),
    ...(gate ? { verification_gate: gate } : {})
  };
  const gen = o.generated_files;
  if (Array.isArray(gen) && gen.length > 0) {
    const files = gen.map((f: Record<string, unknown>) => ({
      file_path: String(f.path ?? f.file_path ?? '—'),
      diff: diffFromContent(String(f.content ?? ''))
    }));
    return { kind: 'coder', files, ...extras };
  }
  const changed = o.files_changed;
  if (Array.isArray(changed) && changed.length > 0) {
    const files = changed.map((path) => ({
      file_path: String(path),
      diff: [] as DiffLine[]
    }));
    return { kind: 'coder', files, ...extras };
  }
  if (self_verification || gate) {
    return { kind: 'coder', files: [], ...extras };
  }
  return null;
}

function coderSelfVerification(
  o: Record<string, unknown>
): CoderOutput['self_verification'] {
  const sv = o.self_verification as Record<string, unknown> | undefined;
  if (!sv || typeof sv !== 'object') return null;
  return {
    ran: Boolean(sv.ran),
    passed: Boolean(sv.passed),
    advisory_unverified: Boolean(sv.advisory_unverified),
    loops_run: Number.isFinite(Number(sv.loops_run)) ? Number(sv.loops_run) : 0,
    ...(Number.isFinite(Number(sv.max_loops)) ? { max_loops: Number(sv.max_loops) } : {}),
    ...(Number.isFinite(Number(sv.total_loops_run))
      ? { total_loops_run: Number(sv.total_loops_run) }
      : {}),
    final_gate_passed: Boolean(sv.final_gate_passed),
    final_gate_reason: typeof sv.final_gate_reason === 'string' ? sv.final_gate_reason : '',
    final_test_results: selfVerifyTestResults(sv.final_test_results),
    ...(sv.structured_analysis ? { structured_analysis: structuredAnalysis(sv.structured_analysis) } : {})
  };
}

function structuredAnalysis(
  raw: unknown
): NonNullable<CoderOutput['self_verification']>['structured_analysis'] {
  const a = (raw ?? {}) as Record<string, unknown>;
  const findings = Array.isArray(a.findings)
    ? (a.findings as Array<Record<string, unknown>>).map((f) => ({
        what: String(f.what ?? ''),
        why: String(f.why ?? ''),
        evidence: String(f.evidence ?? ''),
        ...(f.expected ? { expected: String(f.expected) } : {}),
        ...(f.actual ? { actual: String(f.actual) } : {}),
        suggested_fix: String(f.suggested_fix ?? ''),
        file: String(f.file ?? ''),
        ac_link: String(f.ac_link ?? ''),
        severity: String(f.severity ?? 'test'),
        blocking: f.blocking !== false
      }))
    : [];
  return {
    summary: typeof a.summary === 'string' ? a.summary : '',
    findings,
    fix_files: Array.isArray(a.fix_files) ? a.fix_files.map((x) => String(x)) : [],
    source: a.source === 'fallback_raw' ? 'fallback_raw' : 'llm'
  };
}

function selfVerifyTestResults(
  raw: unknown
): NonNullable<CoderOutput['self_verification']>['final_test_results'] {
  const tr = (raw ?? {}) as Record<string, unknown>;
  const num = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const cases = Array.isArray(tr.cases)
    ? (tr.cases as Array<Record<string, unknown>>).map((c) => ({
        test_name: String(c.test_name ?? ''),
        passed: Boolean(c.passed)
      }))
    : [];
  const failing_cases = Array.isArray(tr.failing_cases)
    ? (tr.failing_cases as unknown[]).map((c) => String(c))
    : [];
  return {
    tests_total: num(tr.tests_total),
    tests_passed: num(tr.tests_passed),
    tests_failed: num(tr.tests_failed),
    failing_cases,
    cases,
    evidence_available: Boolean(tr.evidence_available)
  };
}

const VALID_SEVERITIES = ['Critical', 'High', 'Medium', 'Low'] as const;

function normalizeLineRange(i: Record<string, unknown>): { start: number; end: number } {
  const lr = i.line_range as Record<string, unknown> | undefined;
  if (lr && typeof lr === 'object') {
    const start = Number(lr.start_line ?? 0);
    const end = Number(lr.end_line ?? start);
    return { start, end };
  }
  const n = Number(i.line_number ?? 0);
  return { start: n, end: n };
}

function normalizeSuggestedFix(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const f = raw as Record<string, unknown>;
    return String(f.description ?? '');
  }
  return '';
}

function normalizeProofTestCase(raw: unknown): string | null {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const p = raw as Record<string, unknown>;
    return [p.input, p.expected_behaviour, p.assertion]
      .filter((x) => typeof x === 'string' && x.length > 0)
      .join(' · ') || null;
  }
  return null;
}

function reviewerOutput(
  o: Record<string, unknown>,
  findings_ledger?: {
    entries?: ReadonlyArray<Record<string, unknown>>;
    resolved_count?: number;
    remaining_count?: number;
  } | null
): ReviewerOutput | null {
  const rawIssues = Array.isArray(o.issues) ? o.issues : [];
  const ledgerEntries = Array.isArray(findings_ledger?.entries)
    ? findings_ledger!.entries!
    : [];
  if (o.review_verdict === undefined && rawIssues.length === 0 && ledgerEntries.length === 0) {
    return null;
  }
  const currentIssues = rawIssues.map((raw: Record<string, unknown>, idx: number) => {
    const i = raw as Record<string, unknown>;
    const { start, end } = normalizeLineRange(i);
    const sevRaw = String(i.severity ?? 'Low');
    const severity = (VALID_SEVERITIES as ReadonlyArray<string>).includes(sevRaw)
      ? sevRaw
      : 'Low';
    return {
      issue_id: String(i.issue_id ?? `ISS-${String(idx + 1).padStart(4, '0')}`),
      category: String(i.category ?? 'general'),
      catalogue_entry_id: String(i.catalogue_entry_id ?? i.catalogue_id ?? ''),
      severity: severity as ReviewerOutput['issues'][number]['severity'],
      file_path: String(i.file_path ?? '—'),
      line_start: start,
      line_end: end,
      impact: String(i.impact ?? i.body ?? ''),
      reproduction_steps: Array.isArray(i.reproduction_steps)
        ? i.reproduction_steps.map((s) => String(s))
        : [],
      suggested_fix: normalizeSuggestedFix(i.suggested_fix),
      proof_test_case: normalizeProofTestCase(i.proof_test_case),
      status: 'open' as ReviewerOutput['issues'][number]['status']
    };
  });

  let issues = currentIssues;
  let ledger: ReviewerOutput['ledger'] = null;
  if (ledgerEntries.length > 0) {
    const byKey = new Map(
      currentIssues.map((iss) => [`${iss.catalogue_entry_id}:${iss.file_path}`, iss])
    );
    issues = ledgerEntries.map((rawEntry, idx) => {
      const e = rawEntry as Record<string, unknown>;
      const key = String(e.finding_key ?? '');
      const existing = byKey.get(key);
      const ledgerStatus = String(e.status ?? 'still_present');
      const status: ReviewerOutput['issues'][number]['status'] =
        ledgerStatus === 'fixed' ? 'fixed' : ledgerStatus === 'new' ? 'new' : 'open';
      if (existing) return { ...existing, status };
      const sevRaw = String(e.severity ?? 'Low');
      const severity = (VALID_SEVERITIES as ReadonlyArray<string>).includes(sevRaw)
        ? sevRaw
        : 'Low';
      return {
        issue_id: key || `LEDGER-${String(idx + 1).padStart(4, '0')}`,
        category: String(e.category ?? 'general'),
        catalogue_entry_id: String(e.catalogue_entry_id ?? ''),
        severity: severity as ReviewerOutput['issues'][number]['severity'],
        file_path: String(e.file_path ?? '—'),
        line_start: 0,
        line_end: 0,
        impact: String(e.description ?? ''),
        reproduction_steps: [],
        suggested_fix: '',
        proof_test_case: null,
        status
      };
    });
    ledger = {
      resolved_count: Number(findings_ledger?.resolved_count ?? 0),
      remaining_count: Number(findings_ledger?.remaining_count ?? 0)
    };
  }

  const rawSummary = o.summary as Record<string, unknown> | undefined;
  const summary =
    rawSummary && typeof rawSummary === 'object'
      ? {
          total_issues: Number(rawSummary.total_issues ?? currentIssues.length),
          by_severity: (rawSummary.by_severity as Record<string, number>) ?? {},
          by_category: (rawSummary.by_category as Record<string, number>) ?? {}
        }
      : null;
  return {
    kind: 'reviewer',
    verdict: typeof o.review_verdict === 'string' ? o.review_verdict : null,
    issues,
    summary,
    ledger
  };
}

function verificationGate(o: Record<string, unknown>): TesterOutput['verification_gate'] {
  const g = o.verification_gate as Record<string, unknown> | undefined;
  if (!g || typeof g !== 'object') return null;
  const rawSteps = Array.isArray(g.steps) ? (g.steps as Array<Record<string, unknown>>) : [];
  return {
    passed: Boolean(g.passed),
    reason: typeof g.reason === 'string' ? g.reason : '',
    evidence_available: Boolean(g.evidence_available),
    steps: rawSteps.map((s) => ({
      name: String(s.name ?? ''),
      command: String(s.command ?? ''),
      exit_code: typeof s.exit_code === 'number' ? s.exit_code : null,
      passed: Boolean(s.passed),
      required: Boolean(s.required),
      skipped: Boolean(s.skipped)
    }))
  };
}

function testerOutput(o: Record<string, unknown>): TesterOutput | null {
  const tr = o.test_results as Record<string, unknown> | undefined;
  const gate = verificationGate(o);
  let summary: TesterOutput['summary'] = null;
  if (tr && typeof tr === 'object') {
    const num = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);
    summary = {
      total_tests: num(tr.total_tests),
      passing_tests: num(tr.passing_tests),
      failing_tests: num(tr.failing_tests),
      unverified_tests: num(tr.unverified_tests),
      flaky_tests: num(tr.flaky_tests),
      tests_covering_findings: num(tr.tests_covering_findings),
      framework: typeof tr.framework === 'string' ? tr.framework : 'unknown'
    };
  }
  if (!summary && !gate) return null;
  return {
    kind: 'tester',
    records: [],
    ...(summary ? { summary } : {}),
    ...(gate ? { verification_gate: gate } : {})
  };
}

function documenterOutput(o: Record<string, unknown>): DocumenterOutput | null {
  const rawReadme = Array.isArray(o.readme_updates) ? o.readme_updates : [];
  const rawDocs = Array.isArray(o.doc_updates) ? o.doc_updates : [];
  const rawComments = Array.isArray(o.doc_comments_added) ? o.doc_comments_added : [];
  const rawWarnings = Array.isArray(o.warnings) ? o.warnings : [];

  const readme_updates: DocumenterReadmeUpdate[] = rawReadme
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      path: typeof r.path === 'string' ? r.path : '',
      change_type: r.change_type === 'created' ? ('created' as const) : ('updated' as const),
      sections_changed: Array.isArray(r.sections_changed)
        ? r.sections_changed.filter((s): s is string => typeof s === 'string')
        : [],
      content: typeof r.content === 'string' ? r.content : ''
    }));
  const doc_updates: DocumenterDocUpdate[] = rawDocs
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      path: typeof r.path === 'string' ? r.path : '',
      change_type:
        r.change_type === 'created'
          ? ('created' as const)
          : r.change_type === 'unchanged'
            ? ('unchanged' as const)
            : ('updated' as const),
      content: typeof r.content === 'string' ? r.content : ''
    }));
  const doc_comments_added = rawComments
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      file_path: typeof r.file_path === 'string' ? r.file_path : '',
      symbol: typeof r.symbol === 'string' ? r.symbol : '',
      comment_block: typeof r.comment_block === 'string' ? r.comment_block : '',
      style: typeof r.style === 'string' ? r.style : undefined
    }));
  const warnings = rawWarnings.filter((w): w is string => typeof w === 'string');

  const hasStructured =
    readme_updates.length > 0 || doc_updates.length > 0 || doc_comments_added.length > 0;

  const dd = o.doc_diff;
  const summaryText = typeof dd === 'string' ? dd : dd == null ? '' : JSON.stringify(dd);
  const doc_diffs =
    !hasStructured && summaryText.length > 0
      ? [
          {
            doc_path: 'README.md',
            diff: [{ kind: 'add' as const, old_line: null, new_line: 1, text: summaryText }],
            stale_flags: []
          }
        ]
      : [];

  if (!hasStructured && doc_diffs.length === 0 && warnings.length === 0) return null;

  return { kind: 'documenter', doc_diffs, readme_updates, doc_updates, doc_comments_added, warnings };
}

function coerceOutput(
  outputs: Record<string, unknown> | null | undefined,
  role: AgentRole,
  findings_ledger?: RawStageDetail['findings_ledger']
): RoleOutput | null {
  if (!outputs || typeof outputs !== 'object') return null;
  const kind = (outputs as { kind?: unknown }).kind;
  if (typeof kind === 'string' && KNOWN_OUTPUT_KINDS.has(kind)) {
    return outputs as unknown as RoleOutput;
  }
  switch (role) {
    case 'explorer':
      return explorerOutput(outputs);
    case 'architect':
      return architectOutput(outputs);
    case 'coder':
      return coderOutput(outputs);
    case 'reviewer':
      return reviewerOutput(outputs, findings_ledger);
    case 'tester':
      return testerOutput(outputs);
    case 'documenter':
      return documenterOutput(outputs);
    default:
      return null;
  }
}

export function normalizeStageDetail(raw: RawStageDetail): StageDetailDto {
  const role = raw.role ?? roleForStage(raw.stage_id, undefined);
  const firstArtefact = raw.artefacts?.[0];
  const pipeline_run_id = raw.pipeline_run_id ?? '';
  const raw_output_artefact_id =
    raw.artefacts?.find((a) => a.is_raw_output === true && typeof a.artefact_id === 'string')
      ?.artefact_id ?? null;

  const provenance: StageDetailDto['provenance'] =
    raw.provenance ?? {
      produced_by_role: (firstArtefact?.produced_by_role as AgentRole | undefined) ?? role,
      produced_by_agent_id: firstArtefact?.produced_by_agent_id ?? raw.agent_id ?? '—',
      produced_at: firstArtefact?.produced_at ?? raw.completed_at ?? raw.started_at ?? '',
      pipeline_run_id,
      parent_artefact_ref: firstArtefact?.parent_artefact_ref ?? null
    };

  return {
    pipeline_run_id,
    stage_id: raw.stage_id,
    role,
    state: raw.state as StageDetailDto['state'],
    outcome: (raw.outcome as StageDetailDto['outcome']) ?? null,
    started_at: raw.started_at ?? null,
    completed_at: raw.completed_at ?? null,
    attempt_number: raw.attempt_number ?? 1,
    provenance,
    output:
      raw.output !== undefined
        ? raw.output
        : coerceOutput(raw.outputs, role, raw.findings_ledger),
    sub_progress: raw.sub_progress ?? null,
    back_edge: raw.back_edge ?? null,
    partial_failure: raw.partial_failure ?? null,
    raw_output_artefact_id
  };
}
